/**
 * Utilities for decoding JWT token expiry without external libraries.
 * Used for proactive token refresh scheduling.
 */

/**
 * Decode a base64url-encoded string (JWT uses base64url, not standard base64).
 */
function base64UrlDecode(str: string): string {
  // Replace base64url characters with standard base64 equivalents
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  // Pad with '=' to make length a multiple of 4
  const pad = base64.length % 4;
  if (pad) {
    base64 += "=".repeat(4 - pad);
  }
  return atob(base64);
}

/**
 * Parse the `exp` claim (seconds since epoch) from a JWT access token.
 * Returns null if the token is malformed or has no exp claim.
 */
export function parseTokenExpiry(token: string): number | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Check whether a JWT access token is expired (or will expire within the given buffer).
 */
export function isTokenExpired(token: string, bufferSeconds = 0): boolean {
  const exp = parseTokenExpiry(token);
  if (exp === null) return true; // treat unparseable tokens as expired
  return exp - bufferSeconds <= Date.now() / 1000;
}

/**
 * Milliseconds remaining until the token expires.
 * Returns a negative number if the token is already expired.
 */
export function msUntilExpiry(token: string): number {
  const exp = parseTokenExpiry(token);
  if (exp === null) return -1;
  return exp * 1000 - Date.now();
}
